import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Loader2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdminTitle, Spinner } from "@/admin/AdminLayout";
import { renderField } from "@/admin/fields";

const SEO = [
  { key: "seo_title", label: "SEO başlığı", hint: "Google'da görünen başlık (ideal 50-60 karakter)" },
  { key: "seo_description", label: "SEO açıklaması", type: "area", rows: 2, hint: "Google'da görünen açıklama (ideal 140-160 karakter)" },
];
const ICONS = [["building", "Bina"], ["hotel", "Otel"], ["factory", "Fabrika"], ["briefcase", "İş merkezi"], ["event", "Etkinlik"], ["warehouse", "Depo"], ["construction", "Şantiye"], ["door", "Giriş kontrol"], ["vip", "VIP"], ["camera", "Kamera"], ["consult", "Danışmanlık"], ["industry", "OSB"]];

export const SCHEMAS = {
  services: {
    title: "Hizmetler", path: "/hizmetler/", label: (i) => i.title, empty: { active: true, order: 99, needs: [], steps: [], benefits: [], faqs: [] },
    fields: [
      { key: "title", label: "Hizmet adı" }, { key: "slug", label: "Adres (slug)", hint: "Örn. site-guvenligi → /hizmetler/site-guvenligi" },
      { key: "short", label: "Kısa açıklama (kartlarda)", type: "area", rows: 2 }, { key: "image", label: "Görsel", type: "image" },
      { key: "icon", label: "Simge", type: "select", options: ICONS }, { key: "intro", label: "Hizmet tanımı", type: "area", rows: 5 },
      { key: "needs", label: "Karşıladığı ihtiyaçlar", type: "lines" }, { key: "approach", label: "Uygulama ve planlama yaklaşımı", type: "area", rows: 4 },
      { key: "risk", label: "Risk değerlendirmesinin önemi", type: "area", rows: 4 }, { key: "steps", label: "Planlama aşamaları", type: "lines" },
      { key: "benefits", label: "Faydalar", type: "lines" }, { key: "faqs", label: "Sıkça sorulan sorular", type: "faqs" },
      { key: "order", label: "Sıra", type: "number" }, { key: "active", label: "Yayında", type: "check" }, ...SEO,
    ],
  },
  sectors: {
    title: "Sektör ve Bölge Sayfaları", path: "/hizmetler/", label: (i) => i.title, empty: { active: true, order: 99, sections: [], faqs: [] },
    fields: [
      { key: "title", label: "Sayfa başlığı" }, { key: "slug", label: "Adres (slug)", hint: "Örn. kusadasi-otel-guvenligi" },
      { key: "short", label: "Kısa açıklama", type: "area", rows: 2 }, { key: "image", label: "Görsel", type: "image" },
      { key: "service_slug", label: "İlgili hizmet adresi (slug)", hint: "Sayfanın altında bağlantı olarak gösterilir" },
      { key: "sections", label: "İçerik bölümleri", type: "sections" }, { key: "faqs", label: "Sıkça sorulan sorular", type: "faqs" },
      { key: "order", label: "Sıra", type: "number" }, { key: "active", label: "Yayında", type: "check" }, ...SEO,
    ],
  },
  posts: {
    title: "Blog / Bilgi Merkezi", path: "/blog/", label: (i) => i.title, empty: { status: "draft", sections: [] },
    fields: [
      { key: "title", label: "Başlık" }, { key: "slug", label: "Adres (slug)" }, { key: "excerpt", label: "Özet", type: "area", rows: 2 },
      { key: "image", label: "Kapak görseli", type: "image" },
      { key: "status", label: "Durum", type: "select", options: [["published", "Yayında"], ["draft", "Taslak"]] },
      { key: "legal_review", label: "Mevzuat içeriyor – yayından önce doğrulanmalı", type: "check" },
      { key: "sections", label: "İçerik bölümleri", type: "sections" }, ...SEO,
    ],
  },
  faqs: {
    title: "Sıkça Sorulan Sorular", label: (i) => i.q, empty: { order: 99 },
    fields: [{ key: "q", label: "Soru" }, { key: "a", label: "Cevap", type: "area", rows: 4 }, { key: "order", label: "Sıra", type: "number" }],
  },
};

const slugify = (t) => t.toLocaleLowerCase("tr").replace(/ı/g, "i").replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s").replace(/ö/g, "o").replace(/ç/g, "c").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function Editor({ ctype, item, onClose, onSaved }) {
  const schema = SCHEMAS[ctype];
  const [v, setV] = useState(item);
  const [saving, setSaving] = useState(false);
  const autoSlug = !item.id && schema.fields.some((f) => f.key === "slug");
  const set = (k) => (val) => setV((prev) => ({ ...prev, [k]: val, ...(k === "title" && autoSlug ? { slug: slugify(val) } : {}) }));
  const save = async () => {
    const body = { ...v };
    schema.fields.filter((f) => f.type === "lines").forEach((f) => { body[f.key] = (body[f.key] || []).filter((s) => s.trim()); });
    setSaving(true);
    try {
      if (item.id) await api.put(`/admin/content/${ctype}/${item.id}`, body);
      else await api.post(`/admin/content/${ctype}`, body);
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="content-editor">
      <DialogHeader><DialogTitle>{item.id ? "Düzenle" : "Yeni ekle"}</DialogTitle></DialogHeader>
      <div className="space-y-5">{schema.fields.map((f) => renderField(f, v[f.key], set(f.key)))}</div>
      <div className="sticky bottom-0 flex justify-end gap-2 border-t border-zinc-200 bg-white pt-4">
        <button onClick={onClose} className="btn-outline !py-2">Vazgeç</button>
        <button onClick={save} disabled={saving} className="btn-red !py-2" data-testid="content-save-btn">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet</button>
      </div>
    </DialogContent>
  );
}

function ContentRow({ schema, it, onEdit, onDelete }) {
  const tid = it.slug || it.id;
  const hidden = it.active === false || it.status === "draft";
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`content-row-${tid}`}>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-zinc-900">{schema.label(it)}</p>
        <p className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
          {it.slug && <span>/{it.slug}</span>}
          {hidden && <span className="rounded bg-zinc-200 px-1.5 py-0.5">Taslak / Gizli</span>}
          {it.legal_review && <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800"><AlertTriangle className="h-3 w-3" /> Doğrulanmalı</span>}
        </p>
      </div>
      <div className="flex shrink-0 gap-1">
        {schema.path && it.slug && <a href={`${schema.path}${it.slug}`} target="_blank" rel="noopener noreferrer" className="rounded p-2 text-zinc-500 hover:bg-zinc-100" aria-label="Görüntüle"><ExternalLink className="h-4 w-4" /></a>}
        <button onClick={() => onEdit(it)} className="rounded p-2 text-zinc-700 hover:bg-zinc-100" aria-label="Düzenle" data-testid={`content-edit-${tid}`}><Pencil className="h-4 w-4" /></button>
        <button onClick={() => onDelete(it)} className="rounded p-2 text-red-600 hover:bg-red-50" aria-label="Sil" data-testid={`content-delete-${tid}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

function ContentList({ schema, items, onEdit, onDelete }) {
  if (!items) return <Spinner />;
  return (
    <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white" data-testid="content-list">
      {items.map((it) => <ContentRow key={it.id} schema={schema} it={it} onEdit={onEdit} onDelete={onDelete} />)}
    </ul>
  );
}

export default function ContentManager({ ctype }) {
  const schema = SCHEMAS[ctype];
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`/admin/content/${ctype}`).then((r) => setItems(r.data)), [ctype]);
  useEffect(() => {
    setItems(null);
    load();
  }, [load]);
  const del = async (it) => {
    if (!window.confirm(`"${schema.label(it)}" silinsin mi?`)) return;
    await api.delete(`/admin/content/${ctype}/${it.id}`);
    toast.success("Silindi");
    load();
  };
  return (
    <div>
      <AdminTitle title={schema.title} desc={items ? `${items.length} kayıt` : ""}>
        <button onClick={() => setEdit({ ...schema.empty })} className="btn-red !py-2" data-testid="content-add-btn"><Plus className="h-4 w-4" /> Yeni Ekle</button>
      </AdminTitle>
      <ContentList schema={schema} items={items} onEdit={setEdit} onDelete={del} />
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        {edit && <Editor key={edit.id || "new"} ctype={ctype} item={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      </Dialog>
    </div>
  );
}
